/**
 * Fixed-timestep game loop.
 *
 * Physics is advanced in fixed increments (default 100 Hz) using an accumulator,
 * decoupled from rendering which runs once per animation frame (~60 Hz). This
 * keeps the flight model deterministic and stable regardless of display refresh
 * rate, while the render callback receives an interpolation alpha for smoothness.
 */
export class Loop {
  private accumulator = 0;
  private last = 0;
  private running = false;
  private readonly fixedDt: number;
  private readonly maxFrame = 0.1; // clamp huge gaps (tab switch) to avoid spiral

  // Rolling FPS estimate for the HUD.
  fps = 0;
  private fpsAccum = 0;
  private fpsFrames = 0;

  // Rolling CPU load estimate: fraction of wall-clock frame time spent doing
  // JS work (physics update + render), as a game-style "CPU load" percentage.
  cpuLoadPct = 0;
  private busyAccum = 0;

  constructor(
    private readonly update: (dt: number) => void,
    private readonly render: (alpha: number) => void,
    fixedHz = 100,
  ) {
    this.fixedDt = 1 / fixedHz;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.last = performance.now() / 1000;
    requestAnimationFrame(this.frame);
  }

  stop(): void {
    this.running = false;
  }

  private frame = (nowMs: number): void => {
    if (!this.running) return;
    const now = nowMs / 1000;
    let frameTime = now - this.last;
    this.last = now;
    if (frameTime > this.maxFrame) frameTime = this.maxFrame;

    const workStart = performance.now();
    this.accumulator += frameTime;
    while (this.accumulator >= this.fixedDt) {
      this.update(this.fixedDt);
      this.accumulator -= this.fixedDt;
    }

    this.render(this.accumulator / this.fixedDt);
    const busyTime = (performance.now() - workStart) / 1000;

    // FPS tracking
    this.fpsAccum += frameTime;
    this.fpsFrames++;
    this.busyAccum += busyTime;
    if (this.fpsAccum >= 0.5) {
      this.fps = this.fpsFrames / this.fpsAccum;
      this.cpuLoadPct = (this.busyAccum / this.fpsAccum) * 100;
      this.fpsAccum = 0;
      this.fpsFrames = 0;
      this.busyAccum = 0;
    }

    requestAnimationFrame(this.frame);
  };
}
